(() => {
  const initializeReviews = (root) => {
    if (root.dataset.ikaraoReviewsInitialized === 'true') return;
    root.dataset.ikaraoReviewsInitialized = 'true';

    const setupCarousel = (shell) => {
      const track = shell.querySelector('[data-carousel-track]');
      const controls = shell.querySelector('.ikarao-reviews__carousel-controls');
      const previousButton = controls?.querySelector('[data-carousel-prev]');
      const nextButton = controls?.querySelector('[data-carousel-next]');
      const dots = controls?.querySelector('.ikarao-reviews__carousel-dots');
      let index = 0;

      if (!track || !controls || !previousButton || !nextButton || !dots) return null;

      const positionFor = (item) => item.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
      const visibleItems = () => {
        const items = [...track.children].filter((item) => !item.hidden);
        return shell.dataset.carousel === 'creators'
          ? items.sort((a, b) => positionFor(a) - positionFor(b))
          : items;
      };

      const slidePositions = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        return visibleItems().reduce((positions, item) => {
          const position = Math.max(0, Math.min(positionFor(item), maxScroll));
          const previousPosition = positions[positions.length - 1];
          if (previousPosition === undefined || Math.abs(position - previousPosition) > 1) positions.push(position);
          return positions;
        }, []);
      };

      const render = () => {
        const positions = slidePositions();
        if (!positions.length) {
          controls.classList.add('is-static');
          dots.replaceChildren();
          previousButton.disabled = true;
          nextButton.disabled = true;
          return;
        }

        controls.classList.toggle('is-static', positions.length === 1);
        index = Math.min(index, positions.length - 1);
        if (dots.children.length !== positions.length) {
          dots.replaceChildren(...positions.map((_, itemIndex) => {
            const dot = document.createElement('button');
            dot.className = 'ikarao-reviews__carousel-dot';
            dot.type = 'button';
            dot.role = 'tab';
            dot.setAttribute('aria-label', `Go to slide ${itemIndex + 1} of ${positions.length}`);
            dot.addEventListener('click', () => goTo(itemIndex));
            return dot;
          }));
        }

        [...dots.children].forEach((dot, itemIndex) => {
          const isActive = itemIndex === index;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
        previousButton.disabled = index === 0;
        nextButton.disabled = index === positions.length - 1;
      };

      const goTo = (nextIndex, behavior = 'smooth') => {
        const positions = slidePositions();
        if (!positions.length) return;
        index = Math.max(0, Math.min(nextIndex, positions.length - 1));
        track.scrollTo({ left: positions[index], behavior });
        render();
      };

      const syncFromScroll = () => {
        const positions = slidePositions();
        if (!positions.length) return;
        let closestIndex = 0;
        let closestDistance = Infinity;
        positions.forEach((position, itemIndex) => {
          const distance = Math.abs(position - track.scrollLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = itemIndex;
          }
        });
        if (closestIndex !== index) {
          index = closestIndex;
          render();
        }
      };

      previousButton.addEventListener('click', () => goTo(index - 1));
      nextButton.addEventListener('click', () => goTo(index + 1));
      track.addEventListener('scroll', syncFromScroll, { passive: true });
      render();

      return {
        reset: () => {
          index = 0;
          track.scrollTo({ left: 0, behavior: 'auto' });
          render();
        },
        refresh: () => {
          render();
          syncFromScroll();
        }
      };
    };

    const carouselStates = new Map(
      [...root.querySelectorAll('[data-carousel]')].map((shell) => [shell.dataset.carousel, setupCarousel(shell)])
    );
    const tabs = [...root.querySelectorAll('.ikarao-reviews__tab')];
    const reviewCards = [...root.querySelectorAll('.ikarao-reviews__review-card')];
    const reviewCarousel = carouselStates.get('reviews');
    const tabsTrack = root.querySelector('.ikarao-reviews__tabs');
    const tabsPrevious = root.querySelector('[data-tab-prev]');
    const tabsNext = root.querySelector('[data-tab-next]');
    const mobileQuery = window.matchMedia('(max-width: 767px)');
    let productManuallySelected = false;

    const setProduct = (product) => {
      tabs.forEach((tab) => {
        const isActive = tab.dataset.product === product;
        tab.setAttribute('aria-current', isActive ? 'true' : 'false');
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
      });
      reviewCards.forEach((card) => { card.hidden = card.dataset.product !== product; });
      reviewCarousel?.reset();
    };

    const updateTabArrows = () => {
      if (!tabsTrack || !tabsPrevious || !tabsNext) return;
      tabsPrevious.disabled = tabsTrack.scrollLeft <= 1;
      tabsNext.disabled = tabsTrack.scrollLeft + tabsTrack.clientWidth >= tabsTrack.scrollWidth - 1;
    };

    tabsPrevious?.addEventListener('click', () => {
      tabsTrack?.scrollBy({ left: -Math.max(tabsTrack.clientWidth * .72, 150), behavior: 'smooth' });
    });
    tabsNext?.addEventListener('click', () => {
      tabsTrack?.scrollBy({ left: Math.max(tabsTrack.clientWidth * .72, 150), behavior: 'smooth' });
    });
    tabsTrack?.addEventListener('scroll', updateTabArrows, { passive: true });

    tabs.forEach((tab) => tab.addEventListener('click', () => {
      productManuallySelected = true;
      setProduct(tab.dataset.product);
      tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }));

    const syncDefaultProduct = (event) => {
      if (!productManuallySelected) setProduct(event.matches ? 'break-x2' : 'shell-s1');
      requestAnimationFrame(() => {
        carouselStates.forEach((state) => state?.refresh());
        updateTabArrows();
      });
    };

    setProduct(mobileQuery.matches ? 'break-x2' : 'shell-s1');
    if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', syncDefaultProduct);
    else mobileQuery.addListener(syncDefaultProduct);

    window.addEventListener('resize', () => requestAnimationFrame(() => {
      carouselStates.forEach((state) => state?.refresh());
      updateTabArrows();
    }), { passive: true });

    requestAnimationFrame(() => {
      carouselStates.forEach((state) => state?.refresh());
      updateTabArrows();
    });
  };

  const boot = () => document.querySelectorAll('[data-ikarao-reviews]').forEach(initializeReviews);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
